// Copyright (c) Bentley Systems
import { SelectOption } from "@itwin/itwinui-react";
import { ActionParameterVm } from "../../Action/action-parameter-vm";
import { ActionVm } from "../../Action/action-vm";
import { Parameter } from "../../Action/parameter";
import { EformStructureModel } from "../DataModels/eform-structure-model";
import { EfieldOptions } from "./efield-options";
import { MatchCode } from "./match-code";
import { RecordVm } from "./record-vm";
import { ConfirmationText } from "../../Action/confirmation-text";
import { validateField } from "../../Helpers/helper-functions";

export class EformViewModel {
  private _structureModel: EformStructureModel;

  public header: string | undefined;
  public nodeId: number | undefined;
  public records: RecordVm[] = [];
  public originalRecords: RecordVm[] = [];
  public visibleActionsUp: ActionVm[] = [];
  public visibleActionsDown: ActionVm[] = [];
  public inivisibleHeaderActions: ActionVm[] = [];
  public headerBackAction: ActionVm | undefined;
  public matchCodes: MatchCode[] = [];
  public efieldOptions: EfieldOptions[] = [];
  public keyParameter: Parameter | undefined;
  private _urlParameters: Parameter[];
  /** e.g.catalogue Becs packages - only buttons like tiles and no input fields */
  public isOnlyWithTiles: boolean = false;

  constructor(model: EformStructureModel, urlParameters: Parameter[]) {
    this._structureModel = model;
    this._urlParameters = urlParameters;
    this.header =
      model.efDatas.length > 0 && model.efDatas[0].captionText !== null
        ? model.efDatas[0].captionText
        : undefined;
    this.nodeId = model.nodeId;
    this.inivisibleHeaderActions = this.getHeaderActions(false);
    const headerActions = this.getHeaderActions(true);
    this.visibleActionsUp = headerActions.filter(
      (a) =>
        a.dawPosition === "Up" ||
        a.dawPosition === undefined ||
        a.dawPosition === null,
    );
    this.visibleActionsDown = headerActions.filter(
      (a) => a.dawPosition === "Down",
    );
    this.matchCodes = model.efMatchCodes.map((mc) => new MatchCode(mc));
    this.setComboBoxOptions();
    this.keyParameter = this.getKeyParameterFromUrl(urlParameters);
    if (
      this._structureModel.efRecords.every((r) =>
        r.efFields.every((f) => f.hidden === true),
      ) &&
      this.visibleActionsUp.length > 0 &&
      this.visibleActionsDown.length === 0
    ) {
      this.isOnlyWithTiles = true;
    }
  }

  public setRecordsData(records: any[] = []) {
    this._structureModel.efRecords.forEach((r) => {
      const record = records.find((rd) => rd.id === r.recordId);
      this.records.push(
        new RecordVm(
          r,
          this._urlParameters,
          record?.recordItems,
          this._structureModel.styles,
        ),
      );
      this.originalRecords.push(
        new RecordVm(
          r,
          this._urlParameters,
          record?.recordItems,
          this._structureModel.styles,
        ),
      );
    });
  }

  private setComboBoxOptions() {
    const efieldOptions: EfieldOptions[] = [];
    this.matchCodes.forEach((matchCode) => {
      const options: SelectOption<any>[] = [];
      for (let i = 0; i < matchCode.items.length; i++) {
        options[i] = {
          label: matchCode.items[i].text as string,
          value: matchCode.items[i],
        };
      }
      const newEfieldOptions = new EfieldOptions(
        matchCode.matchCodeId as number,
        matchCode.displayNull,
        options,
      );
      efieldOptions.push(newEfieldOptions);
    });
    this.efieldOptions = efieldOptions;
  }

  private getKeyParameterFromUrl(
    urlParameters: Parameter[],
  ): Parameter | undefined {
    let keyParameter: Parameter | undefined;
    this._structureModel.efRecords.forEach((record) => {
      const keyField = record.efFields.find((f) => f.isKey === true);
      if (keyField !== undefined) {
        const keyParam = urlParameters.find((p) => p.name === keyField.name);
        if (keyParam !== undefined) {
          keyParameter = keyParam;
          return;
        }
      }
    });
    return keyParameter;
  }

  private getHeaderActions(isVisible: boolean) {
    const headerActions: ActionVm[] = [];
    this._structureModel.efActions
      .filter(
        (a) =>
          a.actionType === "H" &&
          a.order !== undefined &&
          a.visible === isVisible,
      )
      .sort((a, b) => (a.order as number) - (b.order as number))
      .forEach((a) => {
        const action = new ActionVm();
        action.isFormAction = true;
        action.caption = a.efActionTexts[0].caption;
        action.isGrayedOut = a.isGrayedOut;
        action.actionId = a.actionId;
        action.hasConfirmation = a.confirm;
        if (action.hasConfirmation) {
          action.confirmationText = new ConfirmationText(a.confirmationText);
        }
        action.toastNotification = a.toastNotification;
        action.targetNodeType = a.targetedNode?.nodeType;
        action.targetNodeId = a.targetedNode?.nodeId;
        action.formBehavior = a.formBehavior;
        action.dawPosition = a.dawPosition;
        action.isBackHeaderButton = a.f_BACK;
        if (a.styleId !== undefined) {
          const style = this._structureModel.styles.find(
            (s) => s.styleId === a.styleId,
          );
          if (style !== undefined) {
            action.style = style;
          }
        }
        if (
          a.efActionParameters !== undefined &&
          a.efActionParameters.length > 0
        ) {
          for (const actionParam of a.efActionParameters) {
            const par = new ActionParameterVm();
            par.actionParamId = actionParam.parameterId;
            par.name = actionParam.name;
            par.value = actionParam.value;
            par.valueType = actionParam.type;
            if (par.valueType === "P") {
              const urlPar = this._urlParameters.find(
                (p) => p.name === par.name,
              );
              if (urlPar != null) {
                par.value = urlPar.value;
              }
            }
            action.actionParameters.push(par);
          }
        }
        if (action.isBackHeaderButton) {
          this.headerBackAction = action;
        } else {
          headerActions.push(action);
        }
      });
    return headerActions;
  }

  public get isFormValid(): boolean {
    let formValid = true;
    this.records.forEach((record) => {
      record.recordItems.forEach((recordItem) => {
        if (formValid === false) {
          return;
        }
        recordItem.fields.forEach((field) => {
          if (field.hidden === false) {
            if (
              field.required === true &&
              (field.selectedValue === undefined ||
                field.selectedValue === null ||
                field.selectedValue === "")
            ) {
              formValid = false;
              return;
            }
            // not required -> can be undefined/null
            if (
              field.selectedValue !== undefined &&
              field.selectedValue !== null
            ) {
              const isValidField = validateField(
                field.selectedValue,
                field.validationType,
              );
              if (isValidField === false) {
                formValid = false;
                return;
              }
            }
          }
        });
      });
    });
    return formValid;
  }

  public get isFormChanged(): boolean {
    if (this.originalRecords.length !== this.records.length) {
      return true;
    }
    for (let i = 0; i < this.originalRecords.length; i++) {
      if (
        this.originalRecords[i].recordItems.length !==
        this.records[i].recordItems.length
      ) {
        return true;
      }

      for (let j = 0; j < this.originalRecords[i].recordItems.length; j++) {
        for (
          let k = 0;
          k < this.originalRecords[i].recordItems[j].fields.length;
          k++
        ) {
          if (
            !this.areFieldsSelectedValueSame(
              this.originalRecords[i].recordItems[j].fields[k].selectedValue,
              this.records[i].recordItems[j].fields[k].selectedValue,
            )
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private areFieldsSelectedValueSame(f1: any, f2: any): boolean {
    if (f1 === f2) {
      return true;
    }
    if (
      (f1 === undefined || f1 === null || f1 === "") &&
      (f2 === undefined || f2 === null || f2 === "")
    ) {
      return true;
    }
    return false;
  }
}
